// Structured, editable content for the /about Knowledge Hub.
// Facts here are verified (Wikidata Q140680161, MusicBrainz, IMDb, Spotify).
// Narrative copy is drafted for review — edit freely.

export const NAME = "Nina Capone";
export const LEGAL_NAME = "Jamilah N. Lawry";

export const ROLES = [
  "Recording Artist",
  "Entrepreneur",
  "Technologist",
  "Media Personality",
  "Speaker",
  "Actress",
];

export const LINKEDIN_EXPERIENCE_URL =
  "https://www.linkedin.com/in/jamilahlawry/details/experience/";

// Verified professional experience (source: LinkedIn). org = clean employer name for schema.
export const EXPERIENCE = [
  { title: "Statistician Clerk — NAMCS & NHAMCS", org: "U.S. Department of Commerce", start: "2019", end: "2021" },
  { title: "Information Technology Manager", org: "Resources for Human Development", start: "2012" },
  { title: "IT Support Technician / Billing Agent (CAE)", org: "Comcast Business", start: "2011" },
  { title: "Regional Marketing Executive Assistant — Vehicle Sales, Service & Marketing Division", org: "General Motors Corporation", location: "Wayne, PA — NE Regional Office", start: "2008" },
  { title: "Management Information Systems Manager", org: "Horizon House", start: "2005" },
];

// Hands-on technology / AI / IT expertise (verifiable via LinkedIn).
export const EXPERTISE = [
  "Artificial Intelligence & automation",
  "IT systems & infrastructure",
  "Web & platform development",
  "Music & media technology",
  "Data & digital strategy",
  "AI-driven business consulting",
];

export const BIOS = {
  short:
    "Nina Capone (Jamilah N. Lawry) is an American hip-hop and R&B recording artist, songwriter, entrepreneur, actress, media personality, and speaker — founder of Mob Mistress Entertainment and In Da Streets Radio.",
  medium:
    "Nina Capone, born Jamilah N. Lawry, is an American hip-hop and R&B recording artist and multi-hyphenate entrepreneur. In 2026 she released two full-length albums — Ova 40 and Audio Porn — under her own label, Mob Mistress Entertainment. Beyond music, she founded My Biz Consulting, the independent broadcast network In Da Streets Radio, the non-profit Inspire Da Streets, and Mob Mistress Publishing, building an independent media ecosystem that spans music, radio, film, tech consulting, and community work. A hands-on technologist, she architected the AI, IT, and software backbone of her ventures herself — expertise she now delivers to clients through My Biz Consulting.",
  long: [
    "Nina Capone — born Jamilah N. Lawry — is an American hip-hop and R&B recording artist, songwriter, entrepreneur, actress, media personality, and speaker. She builds independently and owns her catalog, releasing music through her own label, Mob Mistress Entertainment, and administering it through Mob Mistress Publishing.",
    "As an artist, 2026 marked a defining run: the album Ova 40 arrived April 30, followed by Audio Porn on June 25 — two seventeen-track statements released across every major streaming platform. Her discography also includes a growing body of singles, and her catalog is documented on Spotify, Apple Music, and MusicBrainz.",
    "As an entrepreneur, Nina operates a portfolio of ventures: My Biz Consulting (business and AI-tech consulting for artists and founders), In Da Streets Radio (an independent radio and podcast network, home to the shows 10 Minutes of Talk and Respect The Hustle), and Inspire Da Streets, her non-profit dedicated to artist mental health, tech training, and youth empowerment.",
    "Technology and AI sit at the center of that work. A hands-on technologist and IT expert, Jamilah built the digital, AI, and IT infrastructure behind her businesses herself — from platforms and automation to media systems — rather than outsourcing it. That real-world, built-it-myself experience (verifiable through her LinkedIn professional history) is exactly what she brings to clients through My Biz Consulting, where she advises artists and founders on AI, technology, and scaling independent operations.",
    "Her work extends to film — she appeared as Crystal in Heroin (2016) — and to the stage and screen as a media personality and speaker on independent artistry, the business of music, technology, and community empowerment. Nina Capone represents a new model of the fully independent, self-owned creative mogul.",
  ],
};

// Timeline — known facts kept; unknown dates flagged as "Date to be confirmed".
export const TIMELINE = [
  {
    phase: "Career Begins",
    date: "1991",
    text: "Enters the game in Philadelphia \u2014 the first writes, the first stages, the foundation for everything to come.",
  },
  {
    phase: "First Tour \u2014 Lay-D-Bug",
    date: "1992",
    text: "Hits the road on her debut tour under the stage name \u201cLay-D-Bug,\u201d managed by the late Loni Gamble \u2014 her first manager, mentor, and believer.",
  },
  {
    phase: "First Video On Air",
    date: "1993",
    text: "\u201cWe Are The Young\u201d becomes her first music video to air \u2014 broadcast as a Comcast commercial.",
  },
  {
    phase: "PA Prison Tour",
    date: "1994",
    text: "Tours Pennsylvania state correctional facilities \u2014 performing, listening, and bringing music inside the walls. The defining experience that grounded her artist-as-advocate posture.",
  },
  {
    phase: "First Mixtape \u2014 Miss. Relevant",
    date: "1995",
    text: "Releases her debut mixtape, \u201cMiss. Relevant.\u201d An independent statement that traveled corner-to-corner across the city.",
  },
  {
    phase: "Phat Farm Years Begin",
    date: "2001",
    text: "Tapped to host for Russell Simmons and Phat Farm \u2014 working alongside DJ Tayrok across showcases, mixtapes, and brand activations.",
  },
  {
    phase: "Founded Mob Mistress Entertainment",
    date: "2002",
    text: "Officially launches her own label \u2014 the imprint that would house every release going forward.",
  },
  {
    phase: "Phat Farm Mixtape Feature",
    date: "2003",
    text: "Featured on the Phat Farm Mixtape with DJ Tayrok \u2014 a national co-sign during her Russell Simmons tenure. Same year, launches In Da Streets Radio (formerly Girly Music Radio).",
  },
  {
    phase: "Steel City Massacre & In Da Streets Radio",
    date: "2005",
    text: "Featured on DJ Cadillac\u2019s \u201cSteel City Massacre Vol. 1.\u201d The Phat Farm chapter closes; her own 24/7 global platform for indie artists takes center stage.",
  },
  {
    phase: "My Biz Consulting",
    date: "2010",
    text: "Begins advising creators and small businesses on AI-first operations and the business of independent music.",
  },
  {
    phase: "Film \u2014 Heroin",
    date: "2016",
    text: "Appears as Crystal in the film Heroin (2016) \u2014 an on-screen credit under her legal name, Jamilah Lawry.",
  },
  {
    phase: "Inspire Da Streets",
    date: "2024",
    text: "Founds the non-profit for artist mental health & youth empowerment.",
  },
  {
    phase: "Two Albums, One Era",
    date: "2026",
    text: "Releases two full-length albums self-published via Mob Mistress Entertainment: Ova 40 (April 30) and Audio Porn (June 25) \u2014 17 tracks each.",
  },
];

const SPOTIFY_ALBUM = "https://open.spotify.com/album/";

export const ALBUMS = [
  {
    title: "Audio Porn",
    year: "2026",
    date: "June 25, 2026",
    tracks: 17,
    label: "Mob Mistress Entertainment",
    spotify: `${SPOTIFY_ALBUM}6JIR2LJXlvC6LateWdyxwv`,
    image: "https://i.scdn.co/image/ab67616d0000e1a3170e411e954b76757fa1b1c8",
  },
  {
    title: "Ova 40",
    year: "2026",
    date: "April 30, 2026",
    tracks: 17,
    label: "Mob Mistress Entertainment",
    spotify: `${SPOTIFY_ALBUM}2XDRxKesvCeCc6oS4dfX3g`,
    image: "https://i.scdn.co/image/ab67616d0000e1a3da642225fc93e7eb4a8528b1",
  },
];

export const SINGLES = [
  { title: "I Just Wanna", year: "2026", apple: "https://music.apple.com/us/album/i-just-wanna/1896169993?i=6766031814", art: "https://is1-ssl.mzstatic.com/image/thumb/Music221/v4/e3/69/04/e3690479-2098-d420-87e5-afd5afc9556d/artwork.jpg/600x600bb.jpg" },
  { title: "You Already Know (feat. Montana 3Lak)", year: "2026", apple: "https://music.apple.com/us/album/you-already-know-feat-montana-3lak/1893566629?i=1893566630", art: "https://is1-ssl.mzstatic.com/image/thumb/Music221/v4/bc/5a/a9/bc5aa92d-ca51-d45d-967e-9592486ab91a/artwork.jpg/600x600bb.jpg" },
  { title: "I Wish This Bxtch Would Shut Up (Dance Mix)", year: "2026", apple: "https://music.apple.com/us/album/i-wish-this-bxtch-would-shut-up-dance-mix/1893847885?i=1893848186", art: "https://is1-ssl.mzstatic.com/image/thumb/Music211/v4/c3/6f/50/c36f50ac-6ded-fccd-8f1a-e4e929c9f457/artwork.jpg/600x600bb.jpg" },
  { title: "Stupid", year: "2026", apple: "https://music.apple.com/us/album/stupid/1890057047?i=1890057048", art: "https://is1-ssl.mzstatic.com/image/thumb/Music211/v4/b5/f3/d0/b5f3d026-45cb-45e1-adbc-130ba273a760/artwork.jpg/600x600bb.jpg" },
  { title: "Simple Prayer", year: "2026", apple: "https://music.apple.com/us/album/simple-prayer/1890016888?i=1890016889", art: "https://is1-ssl.mzstatic.com/image/thumb/Music211/v4/00/c8/f1/00c8f1a0-dd3e-bca0-143d-0e238344a48c/artwork.jpg/600x600bb.jpg" },
  { title: "Falsely Accused", year: "2024", apple: "https://music.apple.com/us/album/falsely-accused/1778888549?i=1778888552", art: "https://is1-ssl.mzstatic.com/image/thumb/Music211/v4/55/29/88/552988f2-3770-e725-97c2-b51b86487c43/198846839714.jpg/600x600bb.jpg" },
  { title: "MIND OVER MATTER (feat. Montana 3lak)", year: "2024", apple: "https://music.apple.com/us/album/mind-over-matter-feat-montana-3lak/1766264769?i=1766264770", art: "https://is1-ssl.mzstatic.com/image/thumb/Music221/v4/87/bc/f8/87bcf81c-dab2-c8b2-d82d-96dbc97df1d9/artwork.jpg/600x600bb.jpg" },
  { title: "MURDA (feat. Montana 3lak)", year: "2024", apple: "https://music.apple.com/us/album/murda-feat-montana-3lak/1766147880?i=1766147881", art: "https://is1-ssl.mzstatic.com/image/thumb/Music221/v4/c3/60/57/c360579e-67ef-b64c-a207-80bb6f592889/artwork.jpg/600x600bb.jpg" },
  { title: "GO GETTA", year: "2024", apple: "https://music.apple.com/us/album/go-getta/1766434222?i=1766434223", art: "https://is1-ssl.mzstatic.com/image/thumb/Music221/v4/96/d3/8b/96d38bc1-3a08-ab48-1499-37a350122f88/artwork.jpg/600x600bb.jpg" },
  { title: "Sirens Theme", year: "2022", apple: "https://music.apple.com/us/album/sirens-theme/1648917452?i=1648917453", art: "https://is1-ssl.mzstatic.com/image/thumb/Music112/v4/07/de/41/07de41ce-1fc7-8c94-bbc4-28cca8b31b15/artwork.jpg/600x600bb.jpg" },
  { title: "Money Ova Niggas", year: "2020", apple: "https://music.apple.com/us/album/money-ova-n-s/1522315212?i=1522315213", art: "https://is1-ssl.mzstatic.com/image/thumb/Music114/v4/1e/30/5e/1e305e47-c73c-c816-02d8-e7ca46bfd97c/artwork.jpg/600x600bb.jpg" },
  { title: "Days Like This", year: "2020", apple: "https://music.apple.com/us/album/days-like-this/1516838119?i=1516838120", art: "https://is1-ssl.mzstatic.com/image/thumb/Music113/v4/33/1e/92/331e9210-3c70-3d32-4017-88653eb23271/artwork.jpg/600x600bb.jpg" },
  { title: "I'm Peepin'", year: "2020", apple: "https://music.apple.com/us/album/im-peepin/1517707140?i=1517707141", art: "https://is1-ssl.mzstatic.com/image/thumb/Music113/v4/75/19/12/75191246-98eb-19d8-51f4-ac934e932a64/artwork.jpg/600x600bb.jpg" },
  { title: "Cry No More", year: "2020", apple: "https://music.apple.com/us/album/cry-no-more/1516819998?i=1516819999", art: "https://is1-ssl.mzstatic.com/image/thumb/Music123/v4/b8/43/5e/b8435eb0-63b0-0a07-e50b-403fbd382898/artwork.jpg/600x600bb.jpg" },
  { title: "Clothes OFF", year: "2020", apple: "https://music.apple.com/us/album/clothes-off/1500364927?i=1500364928", art: "https://is1-ssl.mzstatic.com/image/thumb/Music124/v4/c3/ab/d1/c3abd160-0201-09f7-a93b-b05c220fa431/artwork.jpg/600x600bb.jpg" },
  { title: "Snap OUT", year: "2020", apple: "https://music.apple.com/us/album/snap-out/1499422473?i=1499422475", art: "https://is1-ssl.mzstatic.com/image/thumb/Music113/v4/1c/fc/e6/1cfce688-94e5-aaa1-bbec-3adad81efd04/artwork.jpg/600x600bb.jpg" },
  { title: "Breaking It Down (feat. Elz)", year: "2020", apple: "https://music.apple.com/us/album/breaking-it-down-feat-elz/1497742045?i=1497742051", art: "https://is1-ssl.mzstatic.com/image/thumb/Music123/v4/40/79/a3/4079a32f-ea0b-f142-657a-e23226ff8570/artwork.jpg/600x600bb.jpg" },
  { title: "Dope Chic", year: "2020", apple: "https://music.apple.com/us/album/dope-chic/1496229109?i=1496229112", art: "https://is1-ssl.mzstatic.com/image/thumb/Music123/v4/91/37/95/913795a5-e732-44fa-062c-6863b747adbc/artwork.jpg/600x600bb.jpg" },
];

export const FILMOGRAPHY = [
  {
    title: "Heroin",
    year: "2016",
    role: "Crystal",
    credited: "Jamilah Lawry",
    genre: "Drama",
    logline:
      "A Philadelphia father\u2019s uncontrollable drug addiction sets off a chain of events that puts his family\u2019s life in the hands of a ruthless killer.",
    poster: "/api/static/film/heroin.jpg",
    watchUrl: "https://tubitv.com/movies/100027320/heroin",
    watchLabel: "Watch on Tubi",
    url: "https://www.imdb.com/name/nm8397747/",
  },
];

export const PODCASTS = [
  {
    name: "10 Minutes of Talk",
    network: "In Da Streets Radio",
    url: "https://www.youtube.com/@indastreetsradio",
    desc: "Short-form, high-impact conversations — culture, business, and real talk in ten minutes.",
  },
  {
    name: "Respect The Hustle",
    network: "In Da Streets Radio",
    url: "https://www.youtube.com/@indastreetsradio",
    desc: "Long-form interviews and stories celebrating independent grind across music and business.",
  },
];

export const BUSINESSES = [
  { name: "My Biz Consulting", url: "https://www.mybizconsulting.net", desc: "Business and AI-technology consulting for artists, founders, and creative entrepreneurs." },
  { name: "In Da Streets Radio", url: "https://indastreetsradio.com", desc: "Independent radio and podcast network — home of 10 Minutes of Talk and Respect The Hustle." },
  { name: "Inspire Da Streets", url: "https://www.inspiredastreets.org/", desc: "501(c)(3) non-profit raising the floor for artist mental health, tech training, and youth empowerment." },
  { name: "Mob Mistress Entertainment", url: "/music", internal: true, desc: "Nina's independent record label and entertainment company — home of her self-released catalog." },
  { name: "Mob Mistress Publishing", url: "/music", internal: true, desc: "The music publishing arm administering Nina Capone's songwriting and compositions." },
  { name: "Anytime Mailbox (Folcroft)", url: "https://www.anytimemailbox.com/s/folcroft-304-baltimore-avenue", cta: "Get a Mailing Address", desc: "A professional mailing address for artists, entrepreneurs, travelers, and businesses — real street address, mail scanning, and forwarding out of Folcroft, PA." },
];

export const SPEAKING_TOPICS = [
  "Building a fully independent, self-owned music career",
  "The business of music: labels, publishing & ownership",
  "AI & technology for artists and founders",
  "Artist mental health & community empowerment",
  "Launching and scaling an independent media network",
];

export const PROFILES = [
  { label: "Apple Music", url: "https://music.apple.com/us/artist/nina-capone/296313757" },
  { label: "Spotify", url: "https://open.spotify.com/artist/1aAI3BGhCIfzsuqx5kLh6O" },
  { label: "MusicBrainz", url: "https://musicbrainz.org/artist/4b17aeda-1bb6-49d0-9945-15d33233067a" },
  { label: "IMDb", url: "https://www.imdb.com/name/nm8397747" },
  { label: "Wikidata", url: "https://www.wikidata.org/wiki/Q140680161" },
  { label: "Instagram", url: "https://www.instagram.com/ninacapone1" },
  { label: "TikTok", url: "https://www.tiktok.com/@ninacaponemusic" },
  { label: "Facebook", url: "https://www.facebook.com/ninacaponefans" },
  { label: "LinkedIn", url: "https://www.linkedin.com/in/jamilahlawry" },
  { label: "YouTube", url: "https://www.youtube.com/@indastreetsradio" },
];

export const ENTITY_IDS = [
  { label: "Wikidata", value: "Q140680161", url: "https://www.wikidata.org/wiki/Q140680161" },
  { label: "MusicBrainz", value: "4b17aeda-1bb6-49d0-9945-15d33233067a", url: "https://musicbrainz.org/artist/4b17aeda-1bb6-49d0-9945-15d33233067a" },
  { label: "IMDb", value: "nm8397747", url: "https://www.imdb.com/name/nm8397747" },
  { label: "Apple Music", value: "296313757", url: "https://music.apple.com/us/artist/nina-capone/296313757" },
  { label: "Official Website", value: "ninacapone.com", url: "https://ninacapone.com" },
];

export const AUTHORITY_SOURCES = [
  {
    group: "Identity & Knowledge Graph",
    items: [
      { name: "Wikidata", url: "https://www.wikidata.org/wiki/Q140680161", priority: "Critical", note: "Primary machine-readable entity record for Nina Capone." },
      { name: "LinkedIn", url: "https://www.linkedin.com/in/jamilahlawry", priority: "Critical", note: "Connects My Biz Consulting, In Da Streets Radio & the podcast under Jamilah N. Lawry." },
    ],
  },
  {
    group: "Music & Film",
    items: [
      { name: "Apple Music", url: "https://music.apple.com/us/artist/nina-capone/296313757", priority: "Critical", note: "Confirms the Nina Capone catalog — including Ova 40 and Audio Porn." },
      { name: "IMDb", url: "https://www.imdb.com/name/nm8397747/", priority: "Critical", note: "Links the legal identity to acting credits (Crystal in Heroin, 2016)." },
      { name: "SoundClick", url: "https://www.soundclick.com/member/default.cfm?memberID=551393", priority: "Medium", note: "Artist profile dating to 2003 — evidence of career longevity." },
    ],
  },
  {
    group: "Press & Interviews",
    items: [
      { name: "E-Coffee with Experts", url: "https://www.digitalwebsolutions.com/interview/jamilah-n-lawry/", priority: "High", note: "Editorial interview on business, technology & My Biz Consulting." },
      { name: "Collabs / Subkit", url: "https://www.collabs.io/mag/my-biz-consulting/", priority: "High", note: "Founder interview covering the My Biz Consulting origin story." },
      { name: "Rivet360", url: "https://www.rivet360.com/blogs/podcasting-as-a-lead-generation-tool", priority: "High", note: "Guest article on podcasting as an authority-building tool." },
    ],
  },
  {
    group: "Podcast & Recognition",
    items: [
      { name: "10 Minutes of Talk · Apple Podcasts", url: "https://podcasts.apple.com/us/podcast/10-minutes-of-talk/id552424353", priority: "Critical", note: "Directly names the creator: Jamilah Lawry aka Nina Capone." },
      { name: "Worldwide Women's Association", url: "https://www.worldwidewomensassociation.com/new_announcement/jamilah-lawry/", priority: "Medium", note: "Third-party professional confirmation of Jamilah Lawry & My Biz Consulting." },
    ],
  },
];

export const FAQS = {
  "Questions journalists ask": [
    { q: "Who is Nina Capone?", a: "Nina Capone (legal name Jamilah N. Lawry) is an American hip-hop and R&B recording artist, songwriter, entrepreneur, actress, media personality, and speaker. She is the founder of Mob Mistress Entertainment and In Da Streets Radio." },
    { q: "What is her real name?", a: "Her legal name is Jamilah N. Lawry; Nina Capone is her stage name." },
    { q: "What has she released recently?", a: "In 2026 she released two seventeen-track albums: Ova 40 (April 30) and Audio Porn (June 25), both self-released via Mob Mistress Entertainment and available on all major streaming platforms." },
    { q: "Is she an independent artist?", a: "Yes. Nina owns her catalog and releases through her own label, Mob Mistress Entertainment, with publishing administered by Mob Mistress Publishing." },
    { q: "What businesses does she own?", a: "My Biz Consulting, In Da Streets Radio, Inspire Da Streets (non-profit), Mob Mistress Entertainment, and Mob Mistress Publishing." },
    { q: "Has she appeared on screen?", a: "Yes — she appeared as Crystal in the film Heroin (2016), credited as Jamilah Lawry." },
  ],
  "Questions fans ask": [
    { q: "Where can I stream her music?", a: "On Spotify, Apple Music, YouTube Music, Amazon, Tidal, Deezer, and every other major platform. You can find direct links on the Music page." },
    { q: "How do I join the fan club?", a: "Head to the Fan Club page — there's a free Inner Circle membership plus a paid tier with exclusive access." },
    { q: "Does she have a podcast?", a: "Yes — 10 Minutes of Talk and Respect The Hustle, both on her network In Da Streets Radio." },
    { q: "How do I book Nina?", a: "Use the Booking section on this page, or reach out through the booking form for performances, speaking, and consulting." },
  ],
  "Questions AI asks": [
    { q: "What is Nina Capone known for?", a: "She is known as an independent hip-hop and R&B recording artist and multi-venture entrepreneur who owns her catalog and operates an independent media ecosystem spanning music, radio, film, tech consulting, and non-profit work." },
    { q: "What is her Wikidata identifier?", a: "Q140680161. Her MusicBrainz artist ID is 4b17aeda-1bb6-49d0-9945-15d33233067a and her IMDb ID is nm8397747." },
    { q: "What albums has she released?", a: "Full-length albums Ova 40 (2026) and Audio Porn (2026), alongside singles including Stupid, You Already Know, Simple Prayer, Falsely Accused, GO GETTA, and Sirens Theme." },
    { q: "What is Mob Mistress Entertainment?", a: "Mob Mistress Entertainment is Nina Capone's independent record label and entertainment company, which releases and owns her music catalog." },
  ],
};
